import { Box, Stack, Typography } from "@mui/material";
import React, { FC, useMemo, useRef, useState } from "react";
import { TPagination } from "@/types/api";
import { JobFilterConditionPanel } from "@/features/job/components/job-list/JobFilterConditionPanel";
import { FilterConditionToolbar, TSavedCondition as TToolbarSavedCondition } from "@irohana-dev/components";
import { TJobFilterFormExtended, TJobDisplayColumnSetting, defaultJobDisplayColumns } from "@/features/job/components/job-list/job-filter-shared";
import { applyColumnSettings } from "@/features/job/components/job-list/job-filter-condition-adapter";
import { JOB_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/job/constants/common";
import { useDeleteSavedCondition, useSavedConditionList } from "@/api/services/main/saved-condition";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import {
  defaultJobFilterValues,
  normalizeJobMultiSelectFields,
  normalizeJobDateFields,
  isEmptyJobFilterValue,
} from "@/features/job/components/job-list/job-filter-shared";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { SelectedJobPagesPopover, TJobSelectionProps } from "@/features/job/components/job-list/SelectedJobPagesPopover";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { hasDisplayColumnsChanged } from "@/components/commons/filter-condition/filter-condition-adapter-kit";

/** The 「デ」 badge uses the project primary for both scopes, on its lightest tint. */

type Props = {
  selectionProps: TJobSelectionProps;
  pagination?: TPagination;
  onApplyAdvancedFilters?: (filters: TJobFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TJobDisplayColumnSetting[]) => void;
  appliedConditionName?: string;
  appliedConditionId?: number | null;
  onAppliedConditionNameChange?: (name: string) => void;
  onAppliedConditionIdChange?: (id: number | null) => void;
  currentDisplayColumns?: TJobDisplayColumnSetting[] | null;
  currentAdvancedFilters?: TJobFilterFormExtended | null;
};

export const JobListTop: FC<Props> = ({
  selectionProps,
  pagination,
  onApplyAdvancedFilters,
  onApplyDisplaySettings,
  appliedConditionName,
  appliedConditionId,
  onAppliedConditionNameChange,
  onAppliedConditionIdChange,
  currentDisplayColumns,
  currentAdvancedFilters,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filterMode, setFilterMode] = useState<"create" | "edit">("create");
  const { data: savedConditionsData } = useSavedConditionList("job");

  const handleOpenCreateFilter = () => {
    setFilterMode("create");
    setFilterAnchorEl(document.body);
  };

  const handleOpenEditFilter = () => {
    setFilterMode("edit");
    setFilterAnchorEl(document.body);
  };

  const handleFilterPopoverClose = () => {
    setFilterAnchorEl(null);
  };

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const justSavedConditionRef = useRef(false);

  const handleApplyFiltersFromPanel = (filters: TJobFilterFormExtended) => {
    onApplyAdvancedFilters?.(filters);
    // A save applies its own condition right after this, so the create-mode reset would
    // wipe the id/name that were just set.
    if (justSavedConditionRef.current) {
      justSavedConditionRef.current = false;
      return;
    }
    if (filterMode === "create") {
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    }
  };

  const handleConditionSaved = (id: number, title: string) => {
    justSavedConditionRef.current = true;
    onAppliedConditionNameChange?.(title);
    onAppliedConditionIdChange?.(id);
    setFilterMode("edit");
  };

  const appliedCondition =
    filterMode === "edit" && appliedConditionId
      ? savedConditionsData?.data.find((item) => Number(item.id) === Number(appliedConditionId))
      : undefined;

  const activeFilterCount = useMemo(() => {
    if (!currentAdvancedFilters) return 0;
    const { registrationDateFrom, registrationDateTo, ...rest } = currentAdvancedFilters;
    let count = Object.values(rest).filter((v) => !isEmptyJobFilterValue(v)).length;
    if (registrationDateFrom || registrationDateTo) count += 1;
    return count;
  }, [currentAdvancedFilters]);

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const deleteSavedCondition = useDeleteSavedCondition();

  const savedConditions = useMemo<TToolbarSavedCondition[]>(
    () =>
      (savedConditionsData?.data ?? []).map((item) => ({
        id: Number(item.id),
        name: item.title,
        shareScope: item.shareScope ?? "self",
        isDefault: item.isDefault ?? false,
        badgeColor: item.defaultColor ?? undefined,
      })),
    [savedConditionsData],
  );

  // A condition that stored 項目設定 owns the columns too, so picking it restores them.
  // One that did not keeps whatever the user currently has.
  const applySavedColumnSettings = (picked: { includeColumnSettings?: boolean; columnSettings?: unknown }) => {
    if (!picked.includeColumnSettings || !picked.columnSettings) return;
    const settings = picked.columnSettings as { order?: string[]; hidden?: string[] };
    const base = currentDisplayColumns ?? [...defaultJobDisplayColumns];
    const next = applyColumnSettings(base, settings.order ?? [], settings.hidden ?? []);
    onApplyDisplaySettings?.(next);
    localStorage.setItem(JOB_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(next));
  };

  const handleSelectCondition = (id: string | number) => {
    const picked = savedConditionsData?.data.find((item) => Number(item.id) === Number(id));
    if (!picked) return;
    onApplyAdvancedFilters?.({
      ...defaultJobFilterValues,
      ...normalizeJobMultiSelectFields(normalizeJobDateFields(picked.filters ?? {}) as Partial<Record<string, unknown>>),
    } as TJobFilterFormExtended);
    applySavedColumnSettings(picked);
    onAppliedConditionNameChange?.(picked.title);
    onAppliedConditionIdChange?.(Number(picked.id));
  };

  const handleDeleteAppliedCondition = async () => {
    if (!appliedConditionId) return;
    try {
      await deleteSavedCondition.mutateAsync(appliedConditionId);
      toast.success("条件を削除しました。");
      onApplyAdvancedFilters?.({ ...defaultJobFilterValues });
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    } catch (e) {
      showError(e);
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleClearAppliedFilter = () => {
    onApplyAdvancedFilters?.(defaultJobFilterValues);
    onAppliedConditionNameChange?.("");
    onAppliedConditionIdChange?.(null);
    onApplyDisplaySettings?.([...defaultJobDisplayColumns]);
    localStorage.setItem(JOB_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(defaultJobDisplayColumns));
    setIsClearConfirmOpen(false);
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body14Semi">
              全 {formatTotal(pagination?.total) ?? 0} 件中 {Math.min((pagination?.page ?? 1) * (pagination?.perPage ?? 20), pagination?.total ?? 0)}{" "}
              件を表示
            </Typography>
            <Typography variant="body14Semi">│</Typography>
            <SelectedJobPagesPopover {...selectionProps} />
          </Stack>

          <FilterConditionToolbar
            onOpenFilter={handleOpenCreateFilter}
            activeCount={
              activeFilterCount || Number(Boolean(appliedConditionId) || hasDisplayColumnsChanged(currentDisplayColumns, defaultJobDisplayColumns))
            }
            conditions={savedConditions}
            selectedId={appliedConditionId ?? undefined}
            onSelect={handleSelectCondition}
            onClearSelection={() => setIsClearConfirmOpen(true)}
            onEdit={handleOpenEditFilter}
            onDelete={() => setIsDeleteConfirmOpen(true)}
            shareColor={filterConditionShareColor}
          />
        </Stack>
      </Box>

      <JobFilterConditionPanel
        mode={filterMode}
        appliedConditionId={filterMode === "edit" ? appliedConditionId : null}
        appliedConditionName={filterMode === "edit" ? appliedConditionName : ""}
        isOpen={Boolean(filterAnchorEl)}
        onClose={handleFilterPopoverClose}
        onApplyFilters={handleApplyFiltersFromPanel}
        onApplyDisplaySettings={onApplyDisplaySettings}
        initialDisplayColumns={currentDisplayColumns || undefined}
        initialFilters={filterMode === "edit" ? currentAdvancedFilters || undefined : defaultJobFilterValues}
        appliedConditionShareScope={appliedCondition?.shareScope}
        appliedConditionIsDefault={appliedCondition?.isDefault}
        appliedConditionIncludeColumnSettings={appliedCondition?.includeColumnSettings}
        appliedConditionColumnSettings={appliedCondition?.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined}
        onConditionSaved={handleConditionSaved}
      />
      <ConfirmDeleteModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onOk={handleDeleteAppliedCondition}
        message="この条件を削除してもよろしいですか？"
        title=""
      />
      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearAppliedFilter}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};

// Styled Components
